import {
  AlertTriangle,
  Braces,
  CheckCircle2,
  ChevronDown,
  Clipboard,
  Columns3,
  Database,
  Download,
  FileJson2,
  FileSpreadsheet,
  FileUp,
  Languages,
  ListTree,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  Table2,
  XCircle
} from "lucide-react";
import React, { useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  buildOutput,
  detectFormat,
  formatBytes,
  isJsonRecord,
  parseDataset,
  stringifyCell,
  type DataFormat,
  type FormatChoice,
  type JsonValue,
  type ParsedDataset,
  type RowRecord
} from "./dataEngine";
import { samples, type SampleKey } from "./sampleData";
import "./styles.css";

type Locale = "en" | "ko";
type TabKey = "table" | "tree" | "schema" | "output";
type SortState = { column: string; direction: "asc" | "desc" } | null;

const copyText: Record<Locale, Record<string, string>> = {
  en: {
    source: "SOURCE",
    sourceKo: "local input",
    upload: "Upload file",
    paste: "Or paste your data",
    clear: "Clear",
    format: "Format",
    language: "Language",
    detected: "Detected",
    local: "Local mode",
    table: "Table",
    tree: "Tree",
    schema: "Schema",
    output: "Output",
    rows: "Rows",
    filter: "Filter rows",
    columns: "Columns",
    validation: "Validation",
    quality: "Data quality",
    export: "Export",
    copy: "Copy",
    download: "Download",
    valid: "Schema pass",
    invalid: "Needs review",
    noIssues: "No quality issues detected.",
    sample: "Sample",
    privacy: "Your file content is parsed in this browser and is not uploaded by the tool.",
    adSlot: "Local parsing keeps source data inside this browser session",
    guideTitle: "Local structured data checks",
    guideBody:
      "Use the workbench for API payloads, webhook samples, SaaS exports, config files, and CSV handoffs before moving data into production systems.",
    schemaExport: "Inferred JSON Schema",
    empty: "No rows match the current filter."
  },
  ko: {
    source: "소스",
    sourceKo: "로컬 입력",
    upload: "파일 업로드",
    paste: "데이터 붙여넣기",
    clear: "지우기",
    format: "포맷",
    language: "언어",
    detected: "감지됨",
    local: "로컬 모드",
    table: "테이블",
    tree: "트리",
    schema: "스키마",
    output: "출력",
    rows: "행",
    filter: "행 필터",
    columns: "컬럼",
    validation: "검증",
    quality: "데이터 품질",
    export: "내보내기",
    copy: "복사",
    download: "다운로드",
    valid: "스키마 통과",
    invalid: "검토 필요",
    noIssues: "품질 이슈가 감지되지 않았습니다.",
    sample: "샘플",
    privacy: "파일 내용은 이 브라우저에서만 파싱되며 이 도구가 업로드하지 않습니다.",
    adSlot: "원본 데이터는 이 브라우저 세션 안에서만 파싱됩니다",
    guideTitle: "로컬 구조화 데이터 점검",
    guideBody:
      "API 응답, 웹훅 샘플, SaaS CSV, 설정 파일을 운영 시스템에 넣기 전에 표와 스키마로 빠르게 확인하세요.",
    schemaExport: "추론된 JSON 스키마",
    empty: "현재 필터와 일치하는 행이 없습니다."
  }
};

const tabConfig: Array<{ key: TabKey; icon: React.ElementType }> = [
  { key: "table", icon: Table2 },
  { key: "tree", icon: ListTree },
  { key: "schema", icon: Braces },
  { key: "output", icon: FileJson2 }
];

function App() {
  const [locale, setLocale] = useState<Locale>("en");
  const [inputText, setInputText] = useState(samples.json);
  const [formatChoice, setFormatChoice] = useState<FormatChoice>("auto");
  const [fileName, setFileName] = useState("sample.json");
  const [activeTab, setActiveTab] = useState<TabKey>("table");
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState<SortState>(null);
  const [outputFormat, setOutputFormat] = useState<DataFormat>("json");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const text = copyText[locale];

  const dataset = useMemo(() => parseDataset(inputText, formatChoice, fileName), [inputText, formatChoice, fileName]);
  const outputText = useMemo(() => buildOutput(dataset, outputFormat), [dataset, outputFormat]);
  const visibleRows = useMemo(() => filterRows(dataset.rows, filter, sort), [dataset.rows, filter, sort]);
  const detected = inputText.trim() ? detectFormat(inputText, fileName) : "json";

  function loadSample(key: SampleKey) {
    setInputText(samples[key]);
    setFileName(`sample.${key === "yaml" ? "yaml" : key}`);
    setFormatChoice(key);
    setFilter("");
    setOutputFormat(key);
  }

  function handleFile(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string") {
        setInputText(result);
        setFileName(file.name);
        setFormatChoice("auto");
        setFilter("");
      }
    };
    reader.readAsText(file);
  }

  async function copyOutput(value: string) {
    await navigator.clipboard.writeText(value);
  }

  function downloadOutput(format: DataFormat) {
    const content = buildOutput(dataset, format);
    const extension = format === "yaml" ? "yaml" : format;
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileName.replace(/\.[^.]+$/, "") || "structlens-export"}.${extension}`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <div className="brand">
          <div className="brand-mark">
            <Database size={22} aria-hidden="true" />
          </div>
          <div>
            <h1>StructLens <span>Studio</span></h1>
            <p>Local-first JSON / CSV / YAML Workbench</p>
          </div>
        </div>
        <nav className="top-actions" aria-label="Workspace actions">
          <IconButton label="Open" icon={FileUp} onClick={() => fileInputRef.current?.click()} />
          <IconButton label="Copy" icon={Clipboard} onClick={() => void copyOutput(outputText)} />
          <IconButton label="Reset" icon={RefreshCw} onClick={() => loadSample("json")} />
          <a data-google-vignette="false" className="nav-link" href="/about.html">About</a>
          <a data-google-vignette="false" className="nav-link" href="/privacy.html">Privacy</a>
        </nav>
      </header>

      <section className="workspace" aria-label="StructLens data workbench">
        <aside className="source-rail">
          <div className="rail-heading">
            <span>{text.source}</span>
            <small>{text.sourceKo}</small>
          </div>

          <input
            ref={fileInputRef}
            className="hidden-input"
            type="file"
            accept=".json,.csv,.yaml,.yml,.txt,text/*,application/json"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) handleFile(file);
            }}
          />

          <button className="drop-zone" type="button" onClick={() => fileInputRef.current?.click()}>
            <FileUp size={28} aria-hidden="true" />
            <span>{text.upload}</span>
            <small>JSON, CSV, YAML, TXT</small>
          </button>

          <div className="sample-row" aria-label="Sample data">
            {(Object.keys(samples) as SampleKey[]).map((key) => (
              <button key={key} className="sample-chip" type="button" onClick={() => loadSample(key)}>
                {text.sample} {key.toUpperCase()}
              </button>
            ))}
          </div>

          <label className="field-label" htmlFor="source-editor">
            <span>{text.paste}</span>
            <button type="button" onClick={() => setInputText("")}>{text.clear}</button>
          </label>
          <textarea
            id="source-editor"
            className="source-editor"
            spellCheck={false}
            value={inputText}
            onChange={(event) => setInputText(event.target.value)}
          />

          <ControlGroup label={text.format}>
            <SegmentedControl
              value={formatChoice}
              options={[
                ["auto", `AUTO (${detected.toUpperCase()})`],
                ["json", "JSON"],
                ["csv", "CSV"],
                ["yaml", "YAML"]
              ]}
              onChange={(value) => setFormatChoice(value as FormatChoice)}
            />
          </ControlGroup>

          <ControlGroup label={text.language} icon={Languages}>
            <SegmentedControl
              value={locale}
              options={[
                ["en", "EN"],
                ["ko", "KO"]
              ]}
              onChange={(value) => setLocale(value as Locale)}
            />
          </ControlGroup>

          <div className="source-card">
            <dl>
              <InfoRow label="Name" value={fileName} />
              <InfoRow label="Size" value={formatBytes(dataset.stats.sourceBytes)} />
              <InfoRow label={text.detected} value={dataset.detectedFormat.toUpperCase()} />
              <InfoRow label="Parse" value={`${dataset.stats.parseMs}ms`} />
            </dl>
          </div>

          <div className="privacy-note">
            <ShieldCheck size={17} aria-hidden="true" />
            <span>{text.privacy}</span>
          </div>
        </aside>

        <section className="stage">
          <div className="tabbar" role="tablist" aria-label="Data views">
            {tabConfig.map(({ key, icon: Icon }) => (
              <button
                key={key}
                role="tab"
                aria-selected={activeTab === key}
                className={activeTab === key ? "active" : ""}
                type="button"
                onClick={() => setActiveTab(key)}
              >
                <Icon size={18} aria-hidden="true" />
                <span>{text[key]}</span>
              </button>
            ))}
          </div>

          <div className="stage-toolbar">
            <div className="stat-strip">
              <Metric label={text.rows} value={dataset.stats.rowCount.toLocaleString()} />
              <Metric label={text.columns} value={dataset.stats.columnCount.toLocaleString()} />
              <Metric label="Valid" value={`${dataset.stats.validRatio}%`} />
            </div>
            <label className="search-box">
              <Search size={16} aria-hidden="true" />
              <input value={filter} onChange={(event) => setFilter(event.target.value)} placeholder={text.filter} />
            </label>
          </div>

          <div className="stage-body">
            {activeTab === "table" && (
              <TableView rows={visibleRows} columns={dataset.columns} sort={sort} setSort={setSort} emptyLabel={text.empty} />
            )}
            {activeTab === "tree" && <TreePanel value={dataset.source} />}
            {activeTab === "schema" && <SchemaPanel dataset={dataset} schemaTitle={text.schemaExport} />}
            {activeTab === "output" && (
              <OutputPanel
                outputText={outputText}
                outputFormat={outputFormat}
                setOutputFormat={setOutputFormat}
                copyLabel={text.copy}
                downloadLabel={text.download}
                onCopy={() => void copyOutput(outputText)}
                onDownload={() => downloadOutput(outputFormat)}
              />
            )}
          </div>
        </section>

        <aside className="inspector-rail">
          <div className="rail-heading">
            <span>INSPECTOR</span>
            <small>{text.validation}</small>
          </div>

          <StatusPanel dataset={dataset} text={text} />
          <SchemaSummary dataset={dataset} />
          <QualityPanel dataset={dataset} text={text} />

          <div className="export-panel">
            <h2>{text.export}</h2>
            <div className="export-grid">
              {(["json", "csv", "yaml"] as DataFormat[]).map((format) => (
                <button key={format} type="button" onClick={() => downloadOutput(format)}>
                  <Download size={17} aria-hidden="true" />
                  <span>{format.toUpperCase()}</span>
                </button>
              ))}
            </div>
          </div>
        </aside>
      </section>

      <section className="below-workspace" aria-label="StructLens reference">
        <div className="trust-note">
          <Sparkles size={16} aria-hidden="true" />
          <span>{text.adSlot}</span>
        </div>
        <div className="guide-copy">
          <h2>{text.guideTitle}</h2>
          <p>{text.guideBody}</p>
        </div>
      </section>
    </main>
  );
}

function IconButton({ label, icon: Icon, onClick }: { label: string; icon: React.ElementType; onClick: () => void }) {
  return (
    <button className="icon-button" type="button" onClick={onClick} title={label} aria-label={label}>
      <Icon size={18} aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}

function ControlGroup({ label, icon: Icon, children }: { label: string; icon?: React.ElementType; children: React.ReactNode }) {
  return (
    <div className="control-group">
      <div className="control-label">
        {Icon ? <Icon size={15} aria-hidden="true" /> : null}
        <span>{label}</span>
      </div>
      {children}
    </div>
  );
}

function SegmentedControl({
  value,
  options,
  onChange
}: {
  value: string;
  options: Array<[string, string]>;
  onChange: (value: string) => void;
}) {
  return (
    <div className="segmented">
      {options.map(([optionValue, label]) => (
        <button
          key={optionValue}
          type="button"
          className={value === optionValue ? "active" : ""}
          onClick={() => onChange(optionValue)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="metric">
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function TableView({
  rows,
  columns,
  sort,
  setSort,
  emptyLabel
}: {
  rows: RowRecord[];
  columns: string[];
  sort: SortState;
  setSort: (sort: SortState) => void;
  emptyLabel: string;
}) {
  if (rows.length === 0 || columns.length === 0) {
    return <div className="empty-state">{emptyLabel}</div>;
  }

  const limitedRows = rows.slice(0, 300);
  return (
    <div className="table-frame">
      <table>
        <thead>
          <tr>
            <th>#</th>
            {columns.map((column) => (
              <th key={column}>
                <button
                  type="button"
                  onClick={() =>
                    setSort(sort?.column === column && sort.direction === "asc" ? { column, direction: "desc" } : { column, direction: "asc" })
                  }
                >
                  <span>{column}</span>
                  <ChevronDown className={sort?.column === column && sort.direction === "desc" ? "flipped" : ""} size={13} aria-hidden="true" />
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {limitedRows.map((row, rowIndex) => (
            <tr key={stableRowKey(row, rowIndex)}>
              <td>{rowIndex + 1}</td>
              {columns.map((column) => (
                <td key={column}>{stringifyCell(row[column] ?? null)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TreePanel({ value }: { value: JsonValue }) {
  return (
    <div className="tree-panel">
      <TreeNode value={value} label="root" depth={0} />
    </div>
  );
}

function TreeNode({ value, label, depth }: { value: JsonValue; label: string; depth: number }) {
  if (Array.isArray(value)) {
    return (
      <details open={depth < 2}>
        <summary>
          <span>{label}</span>
          <code>array[{value.length}]</code>
        </summary>
        <div className="tree-children">
          {value.map((entry, index) => (
            <TreeNode key={`${label}-${index}`} value={entry} label={`${index}`} depth={depth + 1} />
          ))}
        </div>
      </details>
    );
  }

  if (isJsonRecord(value)) {
    const entries = Object.entries(value);
    return (
      <details open={depth < 2}>
        <summary>
          <span>{label}</span>
          <code>object {entries.length}</code>
        </summary>
        <div className="tree-children">
          {entries.map(([key, entry]) => (
            <TreeNode key={`${label}-${key}`} value={entry} label={key} depth={depth + 1} />
          ))}
        </div>
      </details>
    );
  }

  return (
    <div className="tree-leaf">
      <span>{label}</span>
      <code>{value === null ? "null" : String(value)}</code>
    </div>
  );
}

function SchemaPanel({ dataset, schemaTitle }: { dataset: ParsedDataset; schemaTitle: string }) {
  return (
    <div className="schema-layout">
      <div className="field-list">
        {dataset.fields.map((field) => (
          <article key={field.name} className="field-card">
            <div>
              <h3>{field.name}</h3>
              <p>{Object.entries(field.typeCounts).map(([type, count]) => `${type} ${count}`).join(" / ") || "empty"}</p>
            </div>
            <dl>
              <InfoRow label="Observed" value={field.observed.toString()} />
              <InfoRow label="Missing" value={field.missing.toString()} />
              <InfoRow label="Blank" value={field.blank.toString()} />
              <InfoRow label="Required" value={field.required ? "yes" : "no"} />
            </dl>
          </article>
        ))}
      </div>
      <div className="schema-code">
        <h3>{schemaTitle}</h3>
        <pre>{JSON.stringify(dataset.schema, null, 2)}</pre>
      </div>
    </div>
  );
}

function OutputPanel({
  outputText,
  outputFormat,
  setOutputFormat,
  copyLabel,
  downloadLabel,
  onCopy,
  onDownload
}: {
  outputText: string;
  outputFormat: DataFormat;
  setOutputFormat: (format: DataFormat) => void;
  copyLabel: string;
  downloadLabel: string;
  onCopy: () => void;
  onDownload: () => void;
}) {
  return (
    <div className="output-panel">
      <div className="output-toolbar">
        <SegmentedControl
          value={outputFormat}
          options={[
            ["json", "JSON"],
            ["csv", "CSV"],
            ["yaml", "YAML"]
          ]}
          onChange={(value) => setOutputFormat(value as DataFormat)}
        />
        <div className="output-actions">
          <button type="button" onClick={onCopy}>
            <Clipboard size={16} aria-hidden="true" />
            {copyLabel}
          </button>
          <button type="button" onClick={onDownload}>
            <Download size={16} aria-hidden="true" />
            {downloadLabel}
          </button>
        </div>
      </div>
      <pre className="output-code">{outputText}</pre>
    </div>
  );
}

function StatusPanel({ dataset, text }: { dataset: ParsedDataset; text: Record<string, string> }) {
  const ok = dataset.parseErrors.length === 0 && dataset.schemaValid;
  return (
    <section className={`status-panel ${ok ? "ok" : "error"}`}>
      <h2>{text.validation}</h2>
      <div className="status-main">
        {ok ? <CheckCircle2 size={28} aria-hidden="true" /> : <XCircle size={28} aria-hidden="true" />}
        <div>
          <strong>{ok ? text.valid : text.invalid}</strong>
          <p>{ok ? "The parsed rows match the inferred structure." : dataset.schemaErrors[0] ?? dataset.parseErrors[0]}</p>
        </div>
      </div>
    </section>
  );
}

function SchemaSummary({ dataset }: { dataset: ParsedDataset }) {
  return (
    <section className="summary-panel">
      <h2>Schema</h2>
      <div className="summary-kpis">
        <Metric label="fields" value={dataset.fields.length.toString()} />
        <Metric label="required" value={dataset.fields.filter((field) => field.required).length.toString()} />
      </div>
      <ul>
        {dataset.fields.slice(0, 6).map((field) => (
          <li key={field.name}>
            <span>{field.name}</span>
            <code>{Object.keys(field.typeCounts).join(" | ") || "empty"}</code>
          </li>
        ))}
      </ul>
    </section>
  );
}

function QualityPanel({ dataset, text }: { dataset: ParsedDataset; text: Record<string, string> }) {
  return (
    <section className="quality-panel">
      <h2>{text.quality}</h2>
      <div className="quality-kpis">
        <Metric label="valid" value={`${dataset.stats.validRatio}%`} />
        <Metric label="issues" value={dataset.issues.length.toString()} />
      </div>
      {dataset.issues.length === 0 ? (
        <p className="all-good">{text.noIssues}</p>
      ) : (
        <ul>
          {dataset.issues.slice(0, 5).map((issue, index) => (
            <li key={`${issue.title}-${index}`} className={issue.severity}>
              {issue.severity === "error" ? <XCircle size={15} aria-hidden="true" /> : <AlertTriangle size={15} aria-hidden="true" />}
              <span>
                <strong>{issue.title}</strong>
                <small>{issue.detail}</small>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function filterRows(rows: RowRecord[], filter: string, sort: SortState): RowRecord[] {
  const normalized = filter.trim().toLowerCase();
  const filtered = normalized
    ? rows.filter((row) => Object.values(row).some((value) => stringifyCell(value).toLowerCase().includes(normalized)))
    : [...rows];

  if (!sort) return filtered;
  return filtered.sort((left, right) => {
    const leftValue = stringifyCell(left[sort.column] ?? null);
    const rightValue = stringifyCell(right[sort.column] ?? null);
    const comparison = leftValue.localeCompare(rightValue, undefined, { numeric: true, sensitivity: "base" });
    return sort.direction === "asc" ? comparison : -comparison;
  });
}

function stableRowKey(row: RowRecord, index: number): string {
  const seed = Object.entries(row)
    .slice(0, 3)
    .map(([key, value]) => `${key}:${stringifyCell(value)}`)
    .join("|");
  return `${index}-${seed}`;
}

createRoot(document.getElementById("root")!).render(<App />);
